import { useState, useEffect } from 'react';
import { getAuthHeaders, handleAuthError } from '../utils/authUtils';
import { getApiBaseUrlWithoutPath } from '../../config/environment';

const useCategories = () => {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const apiUrl = getApiBaseUrlWithoutPath();
      
      // Get admin token for authenticated request
      const adminToken = localStorage.getItem('adminToken') || localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json'
      };
      
      // Add authorization header if token exists
      if (adminToken) {
        headers['Authorization'] = `Bearer ${adminToken}`;
      }

      const response = await fetch(`${apiUrl}/api/admin/categories`, {
        method: 'GET',
        headers: headers
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const result = await response.json();
      
      console.log('📥 Categories API Response (Full):', JSON.stringify(result, null, 2));
      console.log('📥 Categories API Response Success:', result?.success);
      console.log('📥 Categories API Response Data:', result?.data);
      console.log('📥 Categories API Response Data Is Array:', Array.isArray(result?.data));
      
      // Extract categories from response
      // Expected format: { success: true, data: [...] }
      let categoriesData = [];
      
      if (result && result.success === true && result.data && Array.isArray(result.data)) {
        // Primary format: { success: true, data: [...] }
        console.log('✅ Found categories in result.data, length:', result.data.length);
        categoriesData = result.data;
      } else if (Array.isArray(result)) {
        // Fallback: result is directly an array
        console.log('✅ Response is direct array, length:', result.length);
        categoriesData = result;
      } else if (result && result.data && Array.isArray(result.data)) {
        // Fallback: result.data is an array
        console.log('✅ Found categories in result.data, length:', result.data.length);
        categoriesData = result.data;
      } else {
        console.error('❌ ERROR: Could not find categories in response');
        console.error('❌ Result:', result);
        throw new Error(result?.message || 'Failed to fetch categories - unexpected response format');
      }
      
      console.log('📋 Extracted categoriesData length:', categoriesData.length);
      
      // Validate that we have categories
      if (!Array.isArray(categoriesData)) {
        console.error('❌ ERROR: categoriesData is not an array:', categoriesData);
        setError('Invalid response format: categories data is not an array');
        setCategories([]);
        setLoading(false);
        return;
      }
      
      // If no categories, set empty array (this is valid - no categories exist yet)
      if (categoriesData.length === 0) {
        console.log('ℹ️ INFO: No categories found in response (empty array)');
        setCategories([]);
        setLoading(false);
        return;
      }
      
      // Helper: get parent ID from category (handles parent/parentCategory as object or string)
      const getParentId = (cat) => {
        const p = cat.parentCategory || cat.parent;
        if (!p) return null;
        if (typeof p === 'object') return String(p._id || p.id || p).trim();
        return String(p).trim();
      };
      
      // Helper: get category ID
      const getCatId = (cat) => String(cat._id || cat.id || '').trim();
      
      // Process each category - ensure _id exists
      const allProcessed = categoriesData.map((cat, index) => {
        if (!cat || typeof cat !== 'object') return null;
        const processed = { ...cat };
        if (!processed._id && processed.id) processed._id = processed.id;
        if (!processed._id) processed._id = `temp-${index}-${Date.now()}`;
        return processed;
      }).filter(c => c !== null);
      
      // Build tree from flat list: attach children to parents based on parent reference
      const allIds = new Set(allProcessed.map(c => getCatId(c)));
      const mainCatsRaw = allProcessed.filter(cat => {
        const parentId = getParentId(cat);
        if (!parentId || parentId === '' || parentId === 'null' || parentId === 'undefined') return true;
        // Orphan: parent not in list - show as main
        if (!allIds.has(parentId)) return true;
        return false;
      });
      const processedCategories = mainCatsRaw.map(mainCat => {
        const main = { ...mainCat };
        main.children = [];
        const mainId = getCatId(main);
        
        // Find all subcategories (parent = main)
        const subs = allProcessed.filter(cat => {
          const parentId = getParentId(cat);
          return parentId && getCatId(cat) !== mainId && parentId === mainId;
        });
        
        main.children = subs.map(subCat => {
          const sub = { ...subCat };
          sub.children = [];
          const subId = getCatId(sub);
          
          // Find all children categories (parent = sub)
          const children = allProcessed.filter(cat => {
            const parentId = getParentId(cat);
            return parentId && getCatId(cat) !== subId && parentId === subId;
          });
          
          sub.children = children.map(c => ({ ...c }));
          return sub;
        });
        
        return main;
      });
      
      console.log('✅ Processed categories:', processedCategories.length, 'categories');
      console.log('📋 Processed categories data:', processedCategories);
      
      if (processedCategories.length === 0) {
        console.error('❌ No valid categories after processing');
        setError('No valid categories found in response');
        setCategories([]);
      } else {
        console.log('✅ Setting categories in state:', processedCategories.length, 'categories');
        console.log('✅ First category:', processedCategories[0]);
        setCategories(processedCategories);
        setError(null);
        console.log('✅ Categories state updated successfully');
      }
    } catch (error) {
      console.error('Categories error:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  return { categories, loading, error, refetch: fetchCategories };
};

export default useCategories;


